import { type FC, type ReactNode, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Icon, type IIconsVector, Typography } from 'shared/ui'
import { AudioPlayer } from 'shared/ui/AudioPlayer/ui/AudioPlayer'
import { type Message } from 'entities/Message/model/Message'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { AICallingLabel } from 'entities/AICalling/ui/AICallingLabel/AICallingLabel'
import { MessageCardType } from 'widgets/MessageCard/ui/types'
import { type CallContentStore } from 'widgets/MessageCard/store/callContentStore'
import { ConversationMessageCardItem } from 'widgets/MessageCard/ui/ConversationMessageCardItem/ConversationMessageCardItem'
import { DateContent } from 'widgets/MessageCard/ui/DateContent/DateContent'
import { CallConference } from 'widgets/MessageCard/ui/CallContent/CallConference/CallConference'
import { AirCollWrapper } from 'widgets/MessageCard/ui/CallContent/AirCollWrapper'
import { IvrContent } from 'widgets/MessageCard/ui/CallContent/CallIvr/IvrContent'
import { AnsweredByLabel } from 'widgets/MessageCard/ui/CallContent/AnsweredByLabel/AnsweredByLabel'
import type { ConversationMessagesStore } from 'widgets/ConversationMessages'
import styles from './styles.module.scss'

type ICallContentProps = {
  message: Message
  isLast?: boolean
  setScrollBottomTrigger: () => void
  callContentStore?: CallContentStore
  conversationMessagesStore?: ConversationMessagesStore
}

export const CallContent: FC<ICallContentProps> = observer(
  ({ callContentStore, message, isLast, setScrollBottomTrigger, conversationMessagesStore }) => {
    const { usersStore, organizationStore } = useEntitiesContext()
    const {
      isOutbound,
      icon,
      record,
      id,
      summary,
      transcriptions,
      transcriptStatus,
      forwardInfo,
      isOnlyRecord,
      airCallLink,
      isCallConference,
      isConversationsHistory,
      status,
      ivrTransition,
      contact_id,
      source,
      source_id,
      answered_id,
      answered_user,
    } = message

    const iconVector: IIconsVector | null = useMemo(() => {
      if (forwardInfo) {
        return 'arrowRight'
      }
      if (icon === 'icon_inbound_call') {
        return 'arrowLeftDown'
      }
      if (icon === 'icon_outbound_call') {
        return 'arrowRightUp'
      }
      if (icon === 'icon_missed_call') {
        return 'missed'
      }
      if (icon === 'icon_voicemail') {
        return 'voicemail'
      }
      if (icon === 'icon_conference_call') {
        return isOutbound ? 'arrowRightUp' : 'arrowLeftDown'
      }
      return null
    }, [isOutbound])

    const text: ReactNode = useMemo(() => {
      if (forwardInfo) {
        return `Forwarded to ${forwardInfo.phoneNumber}`
      }
      if (icon === 'icon_inbound_call') {
        return 'Incoming call'
      }
      if (icon === 'icon_outbound_call') {
        return 'Outgoing call'
      }
      if (icon === 'icon_missed_call') {
        return 'Missed call'
      }
      if (icon === 'icon_voicemail') {
        return 'Voicemail'
      }
      if (icon === 'icon_conference_call') {
        return 'Conference call'
      }
      return ''
    }, [isOutbound])

    if (isCallConference) {
      return <CallConference message={message} iconVector={iconVector} />
    }

    const retryTranscript = () => {
      if (status === 'transcript') return
      callContentStore?.retryTranscript(id)
    }
    const isAi = source === 'ai_calling_agent' && source_id
    const showAnsweredByLabel =
      icon === 'icon_inbound_call' && ((isAi && answered_id === 0) || !!answered_user)

    return (
      <>
        {!isOnlyRecord && (
          <ConversationMessageCardItem message={message} type={MessageCardType.Call}>
            <div
              className={classNames(styles.wrapCallContent, {
                [styles.conversationsHistoryView]: message.isConversationsHistory,
                [styles.inboundCall]: icon === 'icon_inbound_call',
              })}
            >
              {ivrTransition && (
                <IvrContent
                  info={ivrTransition}
                  conversationsHistory={message.isConversationsHistory}
                  conversationMessagesStore={conversationMessagesStore}
                />
              )}
              <div className={styles.wrap}>
                {iconVector && (
                  <div
                    className={classNames(
                      styles.wrapIcon,
                      iconVector === 'missed' && styles.missed
                    )}
                  >
                    <Icon icon={iconVector} tertiary={iconVector !== 'missed'} fontSize={20} />
                  </div>
                )}
                <div className={styles.content}>
                  <div className={styles.header}>
                    <Typography variant={'body-rg-medium'} ariaLabel={'text'}>
                      <AirCollWrapper airCallLink={airCallLink}>{text}</AirCollWrapper>
                    </Typography>
                    {isAi && <AICallingLabel id={source_id} />}
                  </div>
                  <div className={'row4'}>
                    <AnsweredByLabel message={message} />
                    {showAnsweredByLabel && <div className={'point'} />}
                    <DateContent message={message} />
                    {forwardInfo?.duration && (
                      <>
                        <div className={'point'} />
                        <Typography variant={'text-input-helper'} ariaLabel={'duration'}>
                          {forwardInfo?.duration}
                        </Typography>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </ConversationMessageCardItem>
        )}
        {record && (
          <ConversationMessageCardItem
            message={message}
            type={MessageCardType.Call}
            className={classNames({
              [styles.inProgress]: [
                'in_progress',
                'init_auto',
                'init_manual',
                'init_retry',
              ].includes(`${transcriptStatus}`),
              [styles.isOutbound]: isOutbound,
            })}
          >
            <div
              className={classNames(styles.wrapCallContent, {
                [styles.conversationsHistoryView]: isConversationsHistory,
              })}
            >
              {record && (
                <AudioPlayer
                  id={`${id}${record.id}`}
                  url={record.url}
                  is_auto_transcribe={record.is_auto_transcribe}
                  transcriptStatus={transcriptStatus}
                  transcriptions={transcriptions}
                  summary={summary}
                  retryTranscript={retryTranscript}
                  canTranscript={!callContentStore?.isOrganizationOutOfMessage}
                  disableTranscript={
                    usersStore.user?.isViewOnlyRole &&
                    record.transcript_status === null &&
                    !record.is_auto_transcribe
                  }
                  hideTranscriptionButton={isConversationsHistory}
                  setScrollBottomTrigger={isLast ? setScrollBottomTrigger : undefined}
                  isCallAudio
                  record={record}
                  contactId={contact_id}
                  handleTrackOrganizationActivity={
                    organizationStore.handleTrackOrganizationActivity
                  }
                  isHipaaCompliant={organizationStore.isHipaaCompliant}
                  source={source}
                />
              )}
            </div>
          </ConversationMessageCardItem>
        )}
      </>
    )
  }
)
